import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Mail, Phone, Building2, MessageSquare, Tag, Calendar, Save, Trash2, Plus } from "lucide-react";

const COLS = [
  { id: "nouveau", label: "Nouveau", color: "#06B6D4" },
  { id: "contacte", label: "Contacté", color: "#67E8F9" },
  { id: "qualifie", label: "Qualifié", color: "#FFDCA8" },
  { id: "propose", label: "Proposé", color: "#A7F3D0" },
  { id: "gagne", label: "Gagné", color: "#16a34a" },
  { id: "perdu", label: "Perdu", color: "#94a3b8" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/leads/${id}`, patch);
      load();
      return r.data;
    } catch { toast.error("Échec mise à jour"); }
  };

  const onDrop = (col) => (e) => {
    e.preventDefault();
    if (dragId) update(dragId, { status: col });
    setDragId(null);
  };

  return (
    <div data-testid="admin-leads">
      <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Leads — Pipeline</h1>
      <p className="text-sm text-[#64748B] mt-1">Cliquez sur une carte pour ouvrir le détail · glissez-déposez pour changer de colonne.</p>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 overflow-x-auto">
        {COLS.map((col) => {
          const items = leads.filter((l) => l.status === col.id);
          return (
            <div key={col.id} className="kanban-col p-3" data-testid={`leads-col-${col.id}`} onDragOver={(e) => e.preventDefault()} onDrop={onDrop(col.id)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                  <span className="text-xs font-bold tracking-wider uppercase text-[#0A1128]">{col.label}</span>
                </div>
                <span className="text-xs font-bold text-[#64748B]">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.map((l) => (
                  <button
                    key={l.id}
                    draggable
                    onDragStart={() => setDragId(l.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => setActive(l)}
                    data-testid={`lead-card-${l.id}`}
                    className={`kanban-card text-left w-full ${dragId === l.id ? "dragging" : ""}`}
                  >
                    <div className="display font-bold text-sm text-[#0A1128] truncate">{l.name}</div>
                    <div className="text-xs text-[#64748B] truncate">{l.email}</div>
                    {l.company && <div className="text-xs text-[#64748B] truncate">🏢 {l.company}</div>}
                    {l.message && <div className="text-xs text-[#475569] mt-1 line-clamp-2">{l.message}</div>}
                    {l.source && <span className="mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full bg-[#FFDCA8]/40 text-[#0A1128] font-semibold">{l.source}</span>}
                  </button>
                ))}
                {items.length === 0 && <div className="text-xs text-[#64748B] italic text-center py-4">Aucun lead</div>}
              </div>
            </div>
          );
        })}
      </div>

      {active && <LeadDetailModal lead={active} onClose={() => setActive(null)} onSaved={(updated) => { setActive(updated); load(); }} onDeleted={() => { setActive(null); load(); }} />}
    </div>
  );
}

function LeadDetailModal({ lead, onClose, onSaved, onDeleted }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [status, setStatus] = useState(lead.status);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/leads/${lead.id}`, { status, notes });
      toast.success("Lead enregistré");
      onSaved(r.data);
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer définitivement ce lead ?")) return;
    try { await api.delete(`/admin/leads/${lead.id}`); toast.success("Supprimé"); onDeleted(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="lead-detail-modal" className="fixed inset-0 z-50 bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-black/8 p-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Lead</div>
            <h2 className="display text-2xl font-extrabold text-[#0A1128] mt-1 truncate">{lead.name}</h2>
            <div className="text-xs text-[#64748B] mt-1">Créé le {fmtDate(lead.created_at)}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>

        {/* Coordonnées */}
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Info icon={Mail} label="Email" value={<a href={`mailto:${lead.email}`} className="text-[#06B6D4] hover:underline break-all">{lead.email}</a>} />
            {lead.phone && <Info icon={Phone} label="Téléphone" value={<a href={`tel:${lead.phone}`} className="text-[#06B6D4] hover:underline">{lead.phone}</a>} />}
            {lead.company && <Info icon={Building2} label="Entreprise" value={lead.company} />}
            {lead.source && <Info icon={Tag} label="Source" value={<span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-[#FFDCA8]/40 text-[#0A1128] font-semibold uppercase tracking-wider">{lead.source}</span>} />}
            {lead.interest && <Info icon={Tag} label="Intérêt" value={lead.interest} />}
            <Info icon={Calendar} label="Mise à jour" value={fmtDate(lead.updated_at)} />
          </div>

          {/* Message initial */}
          {lead.message && (
            <div className="bg-[#FAFAFA] rounded-2xl p-4 border border-black/5">
              <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.3em] uppercase text-[#64748B] mb-2">
                <MessageSquare size={11} /> Message du client
              </div>
              <p className="text-sm text-[#0A1128] whitespace-pre-wrap">{lead.message}</p>
            </div>
          )}

          {/* Statut + notes */}
          <div>
            <label className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#64748B] mb-1.5 block">Statut</label>
            <select
              data-testid="lead-detail-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm"
            >
              {COLS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#64748B] mb-1.5 block">Notes internes</label>
            <Textarea
              data-testid="lead-detail-notes"
              rows={5}
              placeholder="Ajoutez vos notes : appel, contexte, prochaine action…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <LeadTimeline lead={lead} />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-black/8 p-4 flex flex-wrap items-center gap-2 justify-between">
          <Button onClick={del} variant="outline" className="text-red-500 hover:bg-red-50 border-red-200"><Trash2 size={14} className="mr-1" /> Supprimer</Button>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            <Button data-testid="lead-detail-save" disabled={saving} onClick={save} className="bg-[#0A1128] hover:bg-[#14213d] text-white">
              <Save size={14} className="mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#FAFAFA] rounded-xl p-3 border border-black/5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase text-[#64748B] mb-1">
        <Icon size={11} /> {label}
      </div>
      <div className="text-sm text-[#0A1128] break-words">{value}</div>
    </div>
  );
}


const EVENT_TYPES = [
  { v: "appel",          label: "Appel",            icon: "📞" },
  { v: "rdv_physique",   label: "RDV physique",     icon: "🤝" },
  { v: "visio",          label: "Visio",            icon: "🎥" },
  { v: "repas_affaires", label: "Repas d'affaires", icon: "🍽" },
  { v: "sms",            label: "SMS",              icon: "💬" },
  { v: "whatsapp",       label: "WhatsApp",         icon: "🟢" },
  { v: "email",          label: "Email",            icon: "✉" },
  { v: "note",           label: "Note",             icon: "📝" },
  { v: "autre",          label: "Autre",            icon: "•" },
];

function LeadTimeline({ lead }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("appel");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/admin/leads/${lead.id}/events`); setEvents(r.data || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [lead.id]);

  const add = async () => {
    if (!note.trim()) return toast.error("Ajoutez un résumé");
    setBusy(true);
    try {
      await api.post(`/admin/leads/${lead.id}/events`, { kind, note: note.trim() });
      setNote("");
      toast.success("Échange ajouté à la timeline");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const del = async (eid) => {
    if (!window.confirm("Supprimer cet échange ?")) return;
    try { await api.delete(`/admin/lead-events/${eid}`); load(); }
    catch { toast.error("Erreur"); }
  };

  const labelOf = (k) => (EVENT_TYPES.find((t) => t.v === k) || { label: k, icon: "•" });

  return (
    <div data-testid="lead-timeline-section">
      <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#06B6D4] mb-2">Timeline des échanges</div>
      <div className="bg-[#FAFAFA] rounded-2xl p-3 border border-black/5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 items-start">
          <select data-testid="lead-event-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="border border-black/10 rounded-md px-2.5 py-2 text-sm bg-white">
            {EVENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.icon} {t.label}</option>)}
          </select>
          <Textarea
            data-testid="lead-event-note"
            rows={2}
            placeholder="Résumé de l'échange (ce qui s'est dit, prochaine action, etc.)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button onClick={add} disabled={busy || !note.trim()} className="bg-[#0A1128] hover:bg-[#14213d] text-white whitespace-nowrap" data-testid="lead-event-add">
            <Plus size={14} className="mr-1" />Ajouter
          </Button>
        </div>

        {loading ? (
          <div className="text-xs text-[#64748B] py-3 text-center">Chargement…</div>
        ) : events.length === 0 ? (
          <div className="text-xs text-[#64748B] italic py-2 text-center">Aucun échange enregistré.<br/>Ces interactions seront conservées sur la fiche client si ce lead devient client.</div>
        ) : (
          <ol className="space-y-2 relative border-l-2 border-[#06B6D4]/30 ml-2 pl-3 mt-2">
            {events.map((ev) => {
              const lbl = labelOf(ev.kind);
              const d = ev.happened_at ? new Date(ev.happened_at) : null;
              return (
                <li key={ev.id} className="relative" data-testid={`lead-event-${ev.id}`}>
                  <div className="absolute -left-[19px] top-1.5 w-3 h-3 rounded-full bg-[#06B6D4] border-2 border-white"></div>
                  <div className="bg-white border border-black/8 rounded-xl p-2.5 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#06B6D4]/10 text-[#06B6D4]">{lbl.icon} {lbl.label}</span>
                      <span className="text-[10px] text-[#64748B] tabular-nums">{d ? d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      <button onClick={() => del(ev.id)} className="ml-auto text-[#94a3b8] hover:text-red-500" title="Supprimer"><Trash2 size={12} /></button>
                    </div>
                    {ev.note && <div className="text-[#0A1128] mt-1.5 whitespace-pre-wrap text-sm">{ev.note}</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
